import { numberSchema, stringSchema } from '@engagement-web-app/common/validation-utils';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { format } from 'date-fns';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/Button';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Icon } from '@/components/Icon';
import { InvalidRequest } from '@/components/InvalidRequest';
import { Link } from '@/components/Link';
import { Page } from '@/components/Page';
import { Pagination } from '@/components/Pagination';
import { SearchInput } from '@/components/SearchInput';
import { Table } from '@/components/Table';
import { fetchReportingTasks } from '@/queries/reporting';
import { debounce } from '@/utils';
import * as styles from './index.css';

const FROM = 0;
const LIMIT = 25;
const SEARCH = '';

// Showing a full page's worth (LIMIT) of skeleton rows would be excessive here.
const SKELETON_ROW_COUNT = 8;

const SearchParams = z.object({
    from: numberSchema.default(FROM),
    limit: numberSchema.max(100).default(LIMIT),
    search: stringSchema.default(SEARCH),
});

export const Route = createFileRoute('/system/_auth/reporting/')({
    component: SystemReportingTasksRoute,
    validateSearch: zodValidator(SearchParams),
    search: {
        middlewares: [stripSearchParams({ from: FROM, limit: LIMIT, search: SEARCH })],
    },
    errorComponent: () => <InvalidRequest title="タスク" kind="system" />,
});

function SystemReportingTasksRoute() {
    const navigate = Route.useNavigate();
    const { from, limit, search } = Route.useSearch();
    const [keyword, setKeyword] = useState(search);

    const debouncedSearch = useCallback(
        debounce((search: string) => {
            navigate({ to: '/system/reporting', search: { from: 0, limit, search } });
        }, 300),
        []
    );

    const { data, isError, refetch } = useQuery({
        queryKey: ['tasks', from, limit, search],
        queryFn: () => fetchReportingTasks({ from, limit, search }),
    });

    const currentPage = from / limit + 1;
    const lastPage = Math.ceil((data?.total ?? 0) / limit);

    return (
        <Page.Root>
            <Page.Header title="タスク一覧" />

            <Page.Content>
                <div className={styles.searchRow}>
                    <div className={styles.searchContainer}>
                        <SearchInput
                            placeholder="検索"
                            value={keyword}
                            onChange={(e) => {
                                setKeyword(e.target.value);
                                debouncedSearch(e.target.value);
                            }}
                        />
                    </div>
                    <Button variant="primary" onClick={() => navigate({ to: '/system/reporting/tasks/new' })}>
                        <Icon icon={Plus} />
                        タスクを新規作成
                    </Button>
                </div>

                <ErrorOverlay active={isError} onRetry={refetch}>
                    <Table.Container>
                        {data ? (
                            <Table.Root>
                                <Table.Header>
                                    <Table.HeaderCell>登録日時</Table.HeaderCell>
                                    <Table.HeaderCell>タスク名</Table.HeaderCell>
                                    <Table.HeaderCell>クライアント名</Table.HeaderCell>
                                    <Table.HeaderCell>種別</Table.HeaderCell>
                                    <Table.HeaderCell>ステータス</Table.HeaderCell>
                                    <Table.HeaderCell>担当者</Table.HeaderCell>
                                </Table.Header>
                                <Table.Body>
                                    {data.list.map((task) => (
                                        <Table.Row key={task.uuid}>
                                            <Table.Cell>{format(task.createdAt, 'yyyy-MM-dd HH:mm:ss')}</Table.Cell>
                                            <Table.Cell>
                                                <Link
                                                    to="/system/reporting/tasks/$taskId"
                                                    params={{ taskId: task.uuid }}
                                                >
                                                    {task.name}
                                                </Link>
                                            </Table.Cell>
                                            <Table.Cell>{task.organization ? task.organization.name : ''}</Table.Cell>
                                            <Table.Cell>{task.type.displayName}</Table.Cell>
                                            <Table.Cell>{task.status ? task.status.code.displayName : ''}</Table.Cell>
                                            <Table.Cell>{task.userName}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table.Root>
                        ) : (
                            <Table.Skeleton columns={6} rows={SKELETON_ROW_COUNT} />
                        )}
                    </Table.Container>
                </ErrorOverlay>

                <Pagination
                    currentPage={currentPage}
                    lastPage={lastPage}
                    onPageChange={(page) =>
                        navigate({
                            from: Route.fullPath,
                            search: { from: (page - 1) * limit },
                        })
                    }
                />
            </Page.Content>
        </Page.Root>
    );
}
